"use client";
import React, { useState, useEffect } from "react";
import Textinput from "@/components/ui/Textinput";
import Textarea from "@/components/ui/Textarea";
import Button from "@/components/ui/Button";
import Icon from "@/components/ui/Icon";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Card, CardContent, CardHeader } from "@/components/ui/card-shadcn";
import { AjaxGet, AjaxPost } from "@/util/Ajax";
import useAuthInfo from "@/store/auth";
import Select, { components } from "react-select";
import UploadVerification from "@/components/ui/UploadVerification";
import { Button as ButtonShad } from "@/components/ui/ButtonShadcn";
import Swal from "sweetalert2";
import { useRouter } from "next/navigation";

const steps = [
  {
    id: 1,
    title: "Campaign Details",
  },
  {
    id: 2,
    title: "Campaign Settings",
  },
  {
    id: 3,
    title: "Guidelines",
  },
  {
    id: 4,
    title: "Marketing Materials",
  },
];

const styles = {
  multiValue: (base: any, state: any) => {
    return state.data.isFixed ? { ...base, opacity: "0.5" } : base;
  },
  multiValueLabel: (base: any, state: any) => {
    return state.data.isFixed
      ? { ...base, color: "#626262", paddingRight: 6 }
      : base;
  },
  multiValueRemove: (base: any, state: any) => {
    return state.data.isFixed ? { ...base, display: "none" } : base;
  },
  option: (provided: any) => ({
    ...provided,
    fontSize: "14px",
  }),
};

const OptionComponent = ({ data, ...props }: any) => {
  return (
    <components.Option {...props}>
      <span className="flex items-center space-x-4">
        <span className="flex-1">{data.label}</span>
      </span>
    </components.Option>
  );
};

const platformIcons: { [key: string]: string } = {
  tiktok: "logos:tiktok-icon",
  instagram: "skill-icons:instagram",
  youtube: "logos:youtube-icon",
  facebook: "logos:facebook",
};

// Schema for Step 1: Campaign Details
const detailsSchema = yup.object().shape({
  title: yup.string().required("Campaign title is required"),
  description: yup.string().required("Campaign description is required"),
});

// Schema for Step 2: Campaign Settings
const settingsSchema = yup.object().shape({
  budget: yup
    .number()
    .required("Budget is required")
    .min(100, "Minimum budget is R100"),
});

// Schema for Step 3 & 4
const emptySchema = yup.object().shape({});

const CreateCampaignWizard = () => {
  const [stepNumber, setStepNumber] = useState(0);
  const [logoFile, setLogoFile] = useState<any>(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<any[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<any>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<any>(null);

  // Store form data across steps
  const [formData, setFormData] = useState<any>({});

  // Dropdown data from API
  const [genres, setGenres] = useState<any[]>([]);
  const [platformOptions, setPlatformOptions] = useState<any[]>([]);
  const [regionOptions, setRegionOptions] = useState<any[]>([]);
  const [languageOptions, setLanguageOptions] = useState<any[]>([]);

  const userData = useAuthInfo((state) => state);
  const router = useRouter();

  // Guidelines state
  const [guidelines, setGuidelines] = useState([{ description: "" }]);

  // Marketing Materials state - only tracks file uploads
  const [materials, setMaterials] = useState<{file: any}[]>([
    { file: null },
  ]);

  useEffect(() => {
    if (userData.apiToken) {
      getGenres();
      getPlatformTypes();
      getRegions();
      getLanguages();
    }
  }, [userData]);

  const getGenres = async () => {
    var res = await AjaxGet(userData, `Campaign/GetGenres`);
    if (res.data) {
      var options = res.data.map((item: any) => {
        return {
          label: item.name,
          value: item.encId,
        };
      });
      setGenres(options);
    }
  };

  const getPlatformTypes = async () => {
    var res = await AjaxGet(userData, `Campaign/GetPlatformTypes`);
    if (res.data) {
      var options = res.data.map((item: any) => {
        return {
          label: item.name,
          value: item.encPlatformTypeId,
          icon: platformIcons[item.name.toLowerCase()] || "heroicons:globe-alt",
        };
      });
      setPlatformOptions(options);
    }
  };

  const getRegions = async () => {
    var res = await AjaxGet(userData, `Campaign/Regions`);
    if (res.data) {
      var options = res.data.map((item: any) => {
        return {
          label: item.name,
          value: item.encId,
        };
      });
      setRegionOptions(options);
    }
  };

  const getLanguages = async () => {
    var res = await AjaxGet(userData, `Campaign/Languages`);
    if (res.data) {
      var options = res.data.map((item: any) => {
        return {
          label: item.name,
          value: item.encId,
        };
      });
      setLanguageOptions(options);
    }
  };

  // Find current step schema
  let currentStepSchema;
  switch (stepNumber) {
    case 0:
      currentStepSchema = detailsSchema;
      break;
    case 1:
      currentStepSchema = settingsSchema;
      break;
    default:
      currentStepSchema = emptySchema;
  }

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm({
    resolver: yupResolver(currentStepSchema),
    mode: "all",
  });

  // Guidelines handlers
  const handleAddGuideline = () => {
    setGuidelines([...guidelines, { description: "" }]);
  };

  const handleRemoveGuideline = (index: number) => {
    const updated = guidelines.filter((_, i) => i !== index);
    setGuidelines(updated);
  };

  const handleGuidelineChange = (index: number, value: string) => {
    const updated = guidelines.map((item, i) =>
      i === index ? { description: value } : item
    );
    setGuidelines(updated);
  };

  // Marketing Materials handlers
  const handleAddMaterial = () => {
    setMaterials([...materials, { file: null }]);
  };

  const handleRemoveMaterial = (index: number) => {
    const updated = materials.filter((_, i) => i !== index);
    setMaterials(updated);
  };

  const handleMaterialFileChange = (index: number, files: any) => {
    setMaterials(prev => {
      const updated = prev.map((item, i) =>
        i === index ? { file: files } : item
      );
      return updated;
    });
  };

  // Platform toggle handler
  const handlePlatformToggle = (platformValue: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platformValue)
        ? prev.filter((p) => p !== platformValue)
        : [...prev, platformValue]
    );
  };

  const onSubmit = async (data: any) => {
    const totalSteps = steps.length;
    const isLastStep = stepNumber === totalSteps - 1;

    // Merge current step data with accumulated data
    const updatedFormData = { ...formData, ...data };
    setFormData(updatedFormData);

    // Validation per step
    switch (stepNumber) {
      case 0:
        // Step 1: Validate logo is uploaded
        if (!logoFile || logoFile.length === 0 || !logoFile[0].serverId) {
          Swal.fire({
            title: "Error",
            text: "Please upload a logo before proceeding.",
            icon: "error",
            confirmButtonText: "Ok",
          });
          return;
        }
        break;
      case 1:
        // Step 2: Validate at least one platform selected
        if (selectedPlatforms.length === 0) {
          Swal.fire({
            title: "Error",
            text: "Please select at least one platform.",
            icon: "error",
            confirmButtonText: "Ok",
          });
          return;
        }
        // Validate region selected
        if (!selectedRegion) {
          Swal.fire({
            title: "Error",
            text: "Please select a target region.",
            icon: "error",
            confirmButtonText: "Ok",
          });
          return;
        }
        // Validate language selected
        if (!selectedLanguage) {
          Swal.fire({
            title: "Error",
            text: "Please select a content language.",
            icon: "error",
            confirmButtonText: "Ok",
          });
          return;
        }
        break;
    }

    if (isLastStep) {
      // Final step - create everything
      await createCampaignWithAllData(updatedFormData);
    } else {
      setStepNumber(stepNumber + 1);
    }
  };

  const createCampaignWithAllData = async (data: any) => {
    // Step 1: Create Campaign
    const requestData = {
      encCampaignTypeId: "x", 
      encUserId: userData.userId,
      title: data.title,
      description: data.description,
      logoUrl: logoFile?.[0]?.serverId,
      budgetLeft: parseFloat(data.budget) || 0,
      isCampaignActive: true,
      encGenreIds: selectedGenres.map((g: any) => g.value),
      encPlatformTypeIds: selectedPlatforms,
      encRegionId: selectedRegion?.value || null,
      encLanguageId: selectedLanguage?.value || null,
    };
    
    console.log("Request data:", JSON.stringify(requestData, null, 2));

    var createRes = await AjaxPost(userData, `Campaign/Create`, requestData);
    
    console.log("Response:", createRes);

    if (!createRes.data?.encId) {
      Swal.fire({
        title: "Error",
        text: "Failed to create campaign. Please try again.",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    const newCampaignId = createRes.data.encId;

    // Step 2: Save Guidelines
    const validGuidelines = guidelines.filter(
      (g) => g.description.trim() !== ""
    );

    if (validGuidelines.length > 0) {
      const guidelinesToSend = validGuidelines.map((g, index) => ({
        encCampaignId: newCampaignId,
        description: g.description,
        ordernumber: index + 1,
      }));

      await AjaxPost(userData, `CampaignGuideline/Create`, guidelinesToSend);
    }

    // Step 3: Save Marketing Materials
    const materialsToSend: any[] = [];
    
    materials.forEach((m, index) => {
      if (m.file && (m.file as any)[0]?.serverId) {
        const titleInput = document.getElementById(`material_title_${index}`) as HTMLInputElement;
        const descInput = document.getElementById(`material_desc_${index}`) as HTMLInputElement;
        
        materialsToSend.push({
          encCampaignId: newCampaignId,
          title: titleInput?.value || "Untitled",
          description: descInput?.value || "",
          documentUrl: (m.file as any)[0]?.serverId || "",
        });
      }
    });

    if (materialsToSend.length > 0) {
      console.log("Materials to send:", JSON.stringify(materialsToSend, null, 2));

      var materialsRes = await AjaxPost(
        userData,
        `CampaignMarketingMaterial/Create`,
        materialsToSend
      );
      
      console.log("Materials response:", materialsRes);
    }

    // Success - redirect to review page
    Swal.fire({
      title: "Success",
      text: "Campaign created successfully. Please review and proceed to payment.",
      icon: "success",
      confirmButtonText: "Continue",
    }).then(() => {
      router.push(`/campaigns/review/${newCampaignId}`);
    });
  };

  const handlePrev = () => {
    setStepNumber(stepNumber - 1);
  };

  return (
    <div className="container pt-6 mb-4">
      <Card>
        <CardHeader></CardHeader>
        <CardContent>
          {/* Step Indicator */}
          <div className="flex z-[5] items-center relative justify-center md:mx-8">
            {steps.map((item, i) => (
              <div
                className="relative z-[1] items-center item flex flex-start flex-1 last:flex-none group"
                key={i}
              >
                <div
                  className={`${
                    stepNumber >= i
                      ? "bg-primary-500 text-white ring-primary-500 ring-offset-2 dark:ring-offset-slate-500 dark:bg-slate-900 dark:ring-slate-900"
                      : "bg-white ring-slate-900 ring-opacity-70 text-slate-900 dark:text-slate-300 dark:bg-slate-600 dark:ring-slate-600 text-opacity-70"
                  } transition duration-150 icon-box md:h-12 md:w-12 h-7 w-7 rounded-full flex flex-col items-center justify-center relative z-[66] ring-1 md:text-lg text-base font-medium`}
                >
                  {stepNumber <= i ? (
                    <span>{i + 1}</span>
                  ) : (
                    <span className="text-3xl">
                      <Icon icon="bx:check-double" />
                    </span>
                  )}
                </div>

                <div
                  className={`${
                    stepNumber >= i
                      ? "bg-primary-900 dark:bg-primary-900"
                      : "bg-[#E0EAFF] dark:bg-slate-700"
                  } absolute top-1/2 h-[2px] w-full`}
                ></div>
                <div
                  className={`${
                    stepNumber >= i
                      ? "text-primary-900 dark:text-primary-300"
                      : "text-slate-500 dark:text-slate-300 dark:text-opacity-40"
                  } absolute top-full text-base md:leading-6 mt-3 transition duration-150 md:opacity-100 opacity-0 group-hover:opacity-100`}
                >
                  <span className="w-max">{item.title}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="conten-box">
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Step 1: Campaign Details */}
              {stepNumber === 0 && (
                <div>
                  <div className="grid lg:grid-cols-2 md:grid-cols-2 grid-cols-1 gap-5 pt-10">
                    <div className="lg:col-span-2 md:col-span-2 col-span-1">
                      <h4 className="text-base text-slate-800 dark:text-slate-300 my-6">
                        Campaign Details
                      </h4>
                    </div>

                    <Textinput
                      label="Campaign Title"
                      type="text"
                      placeholder="Enter campaign title"
                      name="title"
                      error={errors.title}
                      register={register}
                    />

                    <div className="lg:col-span-2 md:col-span-2 col-span-1">
                      <Textarea
                        label="Campaign Description"
                        placeholder="Describe your campaign, what you're promoting, and what you expect from creators..."
                        name="description"
                        error={errors.description}
                        register={register}
                        row={4}
                        readonly={false}
                        dvalue=""
                        icon=""
                        disabled={false}
                        id="description"
                        horizontal={false}
                        validate=""
                        msgTooltip={false}
                        description=""
                        cols={50}
                        onChange={undefined}
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="flex flex-col gap-2">
                      <label className="form-label">Campaign Logo</label>
                      <UploadVerification
                        files={logoFile}
                        setFiles={setLogoFile}
                        documentName="Logo"
                        maxFileSize="200MB"
                        acceptedFiles={["*/*"]}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Campaign Settings */}
              {stepNumber === 1 && (
                <div>
                  <div className="grid md:grid-cols-2 grid-cols-1 gap-5">
                    <div className="md:col-span-2 col-span-1 mt-8">
                      <h4 className="text-base text-slate-800 dark:text-slate-300 my-6">
                        Campaign Settings
                      </h4>
                    </div>

                    <Textinput
                      label="Campaign Budget (R)"
                      type="number"
                      placeholder="e.g. 5000"
                      name="budget"
                      error={errors.budget}
                      register={register}
                    />

                    <div className="fromGroup">
                      <label className={`block capitalize form-label`}>
                        Content Genres
                      </label>
                      <Select
                        isMulti
                        options={genres}
                        styles={styles}
                        className="react-select"
                        classNamePrefix="select"
                        isSearchable={true}
                        placeholder="Select genres"
                        value={selectedGenres}
                        onChange={(selected) =>
                          setSelectedGenres(selected as any[])
                        }
                        components={{
                          Option: OptionComponent,
                        }}
                      />
                    </div>

                    <div className="fromGroup">
                      <label className={`block capitalize form-label`}>
                        Target Region
                      </label>
                      <Select
                        options={regionOptions}
                        styles={styles}
                        className="react-select"
                        classNamePrefix="select"
                        isSearchable={true}
                        placeholder="Select target region"
                        value={selectedRegion}
                        onChange={(selected) => setSelectedRegion(selected)}
                        components={{
                          Option: OptionComponent,
                        }}
                      />
                    </div>

                    <div className="fromGroup">
                      <label className={`block capitalize form-label`}>
                        Content Language
                      </label>
                      <Select
                        options={languageOptions}
                        styles={styles}
                        className="react-select"
                        classNamePrefix="select"
                        isSearchable={true}
                        placeholder="Select content language"
                        value={selectedLanguage}
                        onChange={(selected) => setSelectedLanguage(selected)}
                        components={{
                          Option: OptionComponent,
                        }}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block capitalize form-label mb-3">
                        Target Platforms
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {platformOptions.map((platform) => (
                          <div
                            key={platform.value}
                            onClick={() => handlePlatformToggle(platform.value)}
                            className={`cursor-pointer p-4 rounded-lg border-2 transition-all duration-200 flex flex-col items-center justify-center gap-2 ${
                              selectedPlatforms.includes(platform.value)
                                ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                                : "border-gray-200 dark:border-slate-600 hover:border-gray-300"
                            }`}
                          >
                            <Icon icon={platform.icon} className="w-8 h-8" />
                            <span className="text-sm font-medium">
                              {platform.label}
                            </span>
                            {selectedPlatforms.includes(platform.value) && (
                              <Icon
                                icon="heroicons:check-circle-solid"
                                className="w-5 h-5 text-primary-500"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Guidelines */}
              {stepNumber === 2 && (
                <div>
                  <div className="mt-8">
                    <h4 className="text-base text-slate-800 dark:text-slate-300 my-6">
                      Campaign Guidelines
                    </h4>
                    <p className="text-sm text-gray-500 mb-4">
                      Add guidelines for creators to follow when creating
                      content for your campaign
                    </p>
                  </div>

                  {guidelines.map((guideline, index) => (
                    <div
                      key={index}
                      className="grid md:grid-cols-2 grid-cols-1 gap-4 mb-4 items-end"
                    >
                      <Textinput
                        label={`Guideline ${index + 1}`}
                        type="text"
                        placeholder="e.g. Video must be at least 30 seconds long"
                        name={`guideline_${index}`}
                        value={guideline.description}
                        defaultValue={guideline.description}
                        onChange={(e) =>
                          handleGuidelineChange(index, e.target.value)
                        }
                      />
                      <div className="md:col-span-1 text-right">
                        {index > 0 && (
                          <ButtonShad
                            type="button"
                            className="btn-danger"
                            onClick={() => handleRemoveGuideline(index)}
                          >
                            Remove
                          </ButtonShad>
                        )}
                      </div>
                    </div>
                  ))}

                  <div className="mt-2">
                    <ButtonShad
                      type="button"
                      className="btn-outline-primary"
                      onClick={() => handleAddGuideline()}
                    >
                      Add Another Guideline
                    </ButtonShad>
                  </div>
                </div>
              )}

              {/* Step 4: Marketing Materials */}
              {stepNumber === 3 && (
                <div>
                  <div className="mt-8">
                    <h4 className="text-base text-slate-800 dark:text-slate-300 my-6">
                      Marketing Materials
                    </h4>
                    <p className="text-sm text-gray-500 mb-4">
                      Upload materials that creators can use (logos, product
                      images, brand guidelines, etc.)
                    </p>
                  </div>

                  {materials.map((material, index) => (
                    <div
                      key={index}
                      className="border rounded-lg p-4 mb-4 dark:border-slate-600"
                    >
                      <div className="grid md:grid-cols-2 grid-cols-1 gap-4 mb-4">
                        <div className="fromGroup">
                          <label className="block capitalize form-label">
                            Material Title
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. Brand Logo Pack"
                            className="form-control py-2"
                            id={`material_title_${index}`}
                          />
                        </div>
                        <div className="fromGroup">
                          <label className="block capitalize form-label">
                            Description (Optional)
                          </label>
                          <input
                            type="text"
                            placeholder="Brief description of the material"
                            className="form-control py-2"
                            id={`material_desc_${index}`}
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="form-label">Upload File</label>
                        <UploadVerification
                          files={material.file}
                          setFiles={(files: any) => handleMaterialFileChange(index, files)}
                          documentName={`Material ${index + 1}`}
                          maxFileSize="200MB"
                        acceptedFiles={["*/*"]}
                        />
                      </div>

                      {index > 0 && (
                        <div className="text-right">
                          <ButtonShad
                            type="button"
                            className="btn-danger"
                            onClick={() => handleRemoveMaterial(index)}
                          >
                            Remove Material
                          </ButtonShad>
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="mt-2">
                    <ButtonShad
                      type="button"
                      className="btn-outline-primary"
                      onClick={() => handleAddMaterial()}
                    >
                      Add Another Material
                    </ButtonShad>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-10">
                {stepNumber > 0 ? (
                  <Button
                    text="back"
                    className="btn-light"
                    type="button"
                    onClick={handlePrev}
                    isLoading={false}
                    disabled={false}
                    children={null}
                    icon=""
                    link=""
                    div={false}
                  />
                ) : (
                  <div></div>
                )}
                <Button
                  text={stepNumber !== steps.length - 1 ? "next" : "submit"}
                  className="btn-dark"
                  type="submit"
                  isLoading={false}
                  disabled={false}
                  children={null}
                  icon=""
                  link=""
                  onClick={null}
                  div={false}
                />
              </div>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateCampaignWizard;